<template>
  <div
    class="ai-dialog-backdrop"
    @click.self="close"
  >
    <div
      class="ai-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="ai-conflict-title"
    >
      <header class="ai-dialog__header">
        <h3 id="ai-conflict-title">
          {{ title }}
        </h3>
        <button
          ref="close"
          type="button"
          class="ai-icon-button"
          title="Закрыть"
          @click="close"
        >
          <ai-icon name="close" />
        </button>
      </header>

      <p class="ai-dialog__lead">
        Новая запись
        <span
          class="ai-dialog__verdict ai-tone"
          :class="conflict.verdict === 'contradicts' ? 'is-danger' : 'is-warning'"
        >{{ verdict }}</span>
        записи, которая уже есть в памяти. Выберите, что сохранить.
      </p>

      <div class="ai-dialog__compare">
        <section class="ai-dialog__side">
          <h4>Было</h4>
          <p>{{ conflict.text }}</p>
        </section>
        <section class="ai-dialog__side is-new">
          <h4>Новое</h4>
          <p>{{ item.text }}</p>
        </section>
      </div>

      <p
        v-if="reason"
        class="ai-dialog__reason"
      >
        <ai-icon
          name="info"
          :size="14"
        />
        <span>{{ reason }}</span>
      </p>

      <label
        v-if="merging"
        class="ai-field"
      >
        <span class="ai-field__label">
          Объединённая запись
          <span
            class="ai-field__hint"
            :class="{ 'is-over': merged.length > maxText }"
          >{{ merged.length }}/{{ maxText }}</span>
        </span>
        <textarea
          ref="merged"
          v-model="merged"
          class="ai-input"
          rows="4"
        />
      </label>

      <p
        v-if="error"
        class="ai-dialog__error"
      >
        {{ error }}
      </p>

      <div class="ai-dialog__actions">
        <template v-if="merging">
          <button
            type="button"
            class="ai-button"
            :disabled="!!busy"
            @click="merging = false"
          >
            Назад
          </button>
          <button
            type="button"
            class="ai-button is-primary"
            :disabled="!!busy || !merged.trim() || merged.length > maxText"
            @click="run('merge')"
          >
            <span
              v-if="busy === 'merge'"
              class="ai-spinner ai-spinner--small"
            />
            Сохранить объединённую
          </button>
        </template>
        <template v-else>
          <button
            type="button"
            class="ai-button is-ghost ai-dialog__discard"
            :disabled="!!busy"
            @click="run('discard')"
          >
            Не сохранять новую
          </button>
          <button
            type="button"
            class="ai-button"
            :disabled="!!busy"
            @click="startMerge"
          >
            Объединить
          </button>
          <button
            type="button"
            class="ai-button"
            :disabled="!!busy"
            @click="run('keep_both')"
          >
            <span
              v-if="busy === 'keep_both'"
              class="ai-spinner ai-spinner--small"
            />
            Оставить обе
          </button>
          <button
            type="button"
            class="ai-button is-primary"
            :disabled="!!busy"
            @click="run('replace')"
          >
            <span
              v-if="busy === 'replace'"
              class="ai-spinner ai-spinner--small"
            />
            Заменить старую
          </button>
        </template>
      </div>
      <p class="ai-dialog__footnote">
        {{ footnote }}
      </p>
    </div>
  </div>
</template>

<script>
  import * as api from '@/api/methods/assistant';
  import AiIcon from './AiIcon.vue';
  import { MAX_TEXT, VERDICTS } from './memory';

  /** Joins the old and the new text for editing: «Было. Новое». */
  function joined(before, after) {
    const head = String(before || '').trim();
    const tail = String(after || '').trim();
    if (!head) return tail;
    return /[.!?…]$/.test(head) ? `${head} ${tail}` : `${head}. ${tail}`;
  }

  // Conflict of a new or edited memory entry with an active one: the administrator decides what to keep.
  // Emits done({ action, item, other }) after a decision, done(null) when closed without one.
  export default {
    name: 'MemoryConflict',
    components: { AiIcon },
    props: {
      // A pending entry with `conflict: {itemId, verdict, reason, text}`.
      item: { type: Object, required: true },
    },
    data: () => ({ merging: false, merged: '', busy: '', error: '', maxText: MAX_TEXT }),
    computed: {
      conflict() {
        return this.item.conflict || {};
      },
      // The model's explanation, without stray leading punctuation.
      reason() {
        return String(this.conflict.reason || '').replace(/^[\s:;,.—-]+/, '');
      },
      verdict() {
        return VERDICTS[this.conflict.verdict] || 'расходится с';
      },
      title() {
        return this.conflict.verdict === 'contradicts' ? 'Противоречие в памяти' : 'Уточнение записи в памяти';
      },
      footnote() {
        return this.item.status === 'archived'
          ? 'Если закрыть окно, запись останется в архиве.'
          : 'Если закрыть окно, новая запись останется в предложениях до решения.';
      },
    },
    mounted() {
      this.$nextTick(() => this.$refs.close && this.$refs.close.focus());
    },
    methods: {
      startMerge() {
        this.merged = joined(this.conflict.text, this.item.text);
        this.merging = true;
        this.error = '';
        this.$nextTick(() => this.$refs.merged && this.$refs.merged.focus());
      },
      async run(action) {
        this.busy = action;
        this.error = '';
        try {
          const text = action === 'merge' ? this.merged.trim() : undefined;
          const { data } = await api.resolveMemory(this.item.id, action, text);
          this.$emit('done', { action, ...data });
        } catch (error) {
          this.error = api.errorText(error, 'Не удалось сохранить решение');
        } finally {
          this.busy = '';
        }
      },
      close() {
        if (!this.busy) this.$emit('done', null);
      },
    },
  };
</script>

<style lang="scss" scoped>
  // Over the whole assistant window (positioned by .ai-window).
  .ai-dialog-backdrop {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 20;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;

    // Dimmed window under the dialog (the same shade as the sidebar backdrop).
    &::before {
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      background: var(--ui-text);
      opacity: 0.28;
    }
  }

  .ai-dialog {
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 12px;
    box-sizing: border-box;
    width: 640px;
    max-width: 100%;
    max-height: 100%;
    padding: 16px 18px;
    border: 1px solid var(--ui-border);
    border-radius: var(--ui-radius-lg);
    background: var(--ui-surface);
    box-shadow: var(--ui-shadow);
    overflow-y: auto;
  }

  .ai-dialog__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;

    h3 {
      margin: 0;
      color: var(--ui-text);
      font-size: 16px;
      font-weight: 500;
    }
  }

  .ai-dialog__lead {
    margin: 0;
    color: var(--ui-text-muted);
    font-size: 13px;
    line-height: 1.6;
  }

  .ai-dialog__verdict {
    padding: 1px 8px;
    border-radius: 999px;
    font-weight: 500;
    white-space: nowrap;
  }

  .ai-dialog__compare {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
  }

  .ai-dialog__side {
    min-width: 0;
    padding: 10px 12px;
    border: 1px solid var(--ui-border);
    border-radius: var(--ui-radius-sm);
    background: var(--ui-surface-soft);

    &.is-new {
      border-color: var(--ui-primary);
      background: var(--ui-primary-soft);
    }

    h4 {
      margin: 0 0 4px;
      color: var(--ui-text-muted);
      font-size: 12px;
      font-weight: 500;
      text-transform: uppercase;
      letter-spacing: 0.03em;
    }

    p {
      margin: 0;
      color: var(--ui-text);
      font-size: 13px;
      line-height: 1.5;
      overflow-wrap: anywhere;
      white-space: pre-wrap;
    }
  }

  .ai-dialog__reason {
    display: flex;
    gap: 6px;
    margin: 0;
    color: var(--ui-text-muted);
    font-size: 13px;
    line-height: 1.5;

    .ai-icon {
      margin-top: 3px;
    }
  }

  .ai-field__hint.is-over {
    color: var(--ui-danger);
  }

  .ai-dialog__error {
    margin: 0;
    color: var(--ui-danger);
    font-size: 13px;
  }

  .ai-dialog__actions {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: 8px;
  }

  .ai-dialog__discard {
    margin-right: auto;
  }

  .ai-dialog__footnote {
    margin: -4px 0 0;
    color: var(--ui-text-muted);
    font-size: 12px;
  }

  @media (max-width: 600px) {
    .ai-dialog__compare {
      grid-template-columns: 1fr;
    }

    .ai-dialog__actions .ai-button {
      flex: 1 1 auto;
      justify-content: center;
    }

    .ai-dialog__discard {
      margin-right: 0;
    }
  }
</style>
